import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Pause, Trash2, AlertCircle } from 'lucide-react';

/**
 * VoiceRecorder — small, dependency-free voice recorder using MediaRecorder.
 * Props:
 *  - maxSeconds (default 60)
 *  - accent (default 'cyan')  — 'cyan' | 'pink' | 'amber'
 *  - onChange({ base64, mime, duration }) — called whenever recording is created/cleared
 */
const ACCENTS = {
  cyan:  { ring: 'ring-cyan-500/40', bg: 'bg-cyan-500',  bgSoft: 'bg-cyan-500/10',  border: 'border-cyan-500/30',  text: 'text-cyan-300',  hover: 'hover:bg-cyan-400' },
  pink:  { ring: 'ring-pink-500/40', bg: 'bg-pink-500',  bgSoft: 'bg-pink-500/10',  border: 'border-pink-500/30',  text: 'text-pink-300',  hover: 'hover:bg-pink-400' },
  amber: { ring: 'ring-amber-500/40',bg: 'bg-amber-500', bgSoft: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-300', hover: 'hover:bg-amber-400' },
};

export default function VoiceRecorder({ maxSeconds = 60, accent = 'cyan', onChange }) {
  const A = ACCENTS[accent] || ACCENTS.cyan;

  const [status, setStatus] = useState('idle'); // idle | recording | recorded | playing
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0); // seconds
  const [recording, setRecording] = useState(null); // { url, blob, base64, mime, duration }
  const [audioLevel, setAudioLevel] = useState(0); // 0..1

  const mediaRecorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => () => stopEverything(), []);

  const stopEverything = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    analyserRef.current = null;
  };

  const startRecording = async () => {
    setError(null);
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError("Ton navigateur ne supporte pas l'enregistrement audio.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;

      // Audio level visualization
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const drawLevel = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const avg = sum / data.length / 255;
        setAudioLevel(avg);
        rafRef.current = requestAnimationFrame(drawLevel);
      };
      drawLevel();

      // Choose best supported mime
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mime = candidates.find(m => MediaRecorder.isTypeSupported(m)) || 'audio/webm';

      const mr = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 24000 });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        const duration = Math.round((Date.now() - startTsRef.current) / 1000);
        const base64 = await blobToBase64(blob);
        const rec = { url, blob, base64, mime, duration };
        setRecording(rec);
        setStatus('recorded');
        onChange && onChange({ base64, mime, duration });
        stopEverything();
      };

      startTsRef.current = Date.now();
      setElapsed(0);
      mr.start(250);
      setStatus('recording');

      tickRef.current = setInterval(() => {
        const sec = Math.floor((Date.now() - startTsRef.current) / 1000);
        setElapsed(sec);
        if (sec >= maxSeconds) stopRecording();
      }, 250);
    } catch (e) {
      setError(e.name === 'NotAllowedError'
        ? "Permission micro refusée. Active-la dans ton navigateur."
        : "Impossible d'accéder au micro.");
      stopEverything();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const clearRecording = () => {
    if (recording?.url) URL.revokeObjectURL(recording.url);
    setRecording(null);
    setStatus('idle');
    setElapsed(0);
    setAudioLevel(0);
    onChange && onChange(null);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play();
      setStatus('playing');
    } else {
      audioRef.current.pause();
      setStatus('recorded');
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${m}:${ss}`;
  };

  // Live bar segments based on audioLevel
  const levelBars = Array.from({ length: 20 }).map((_, i) => {
    const threshold = (i + 1) / 20;
    return audioLevel >= threshold;
  });

  return (
    <div className={`relative rounded-xl border ${A.border} ${A.bgSoft} p-4`} data-testid="voice-recorder">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg ${A.bgSoft} border ${A.border} flex items-center justify-center flex-shrink-0`}>
          <Mic className={`w-4 h-4 ${A.text}`} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Message vocal <span className="text-zinc-500 text-xs font-normal">(optionnel · {maxSeconds}s max)</span></p>
          <p className="text-xs text-zinc-400 leading-relaxed mt-0.5">
            Présente-toi en quelques secondes et dis pourquoi on devrait te choisir.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {status === 'idle' && (
          <button
            type="button"
            onClick={startRecording}
            className={`inline-flex items-center gap-2 px-4 py-2 ${A.bg} ${A.hover} text-zinc-950 text-sm font-semibold rounded-lg transition-colors`}
            data-testid="voice-record-start"
          >
            <Mic className="w-3.5 h-3.5" strokeWidth={2.5} />
            Enregistrer
          </button>
        )}

        {status === 'recording' && (
          <>
            <button
              type="button"
              onClick={stopRecording}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="voice-record-stop"
            >
              <Square className="w-3.5 h-3.5" strokeWidth={2.5} />
              Arrêter
            </button>
            <div className="flex items-center gap-2 text-sm font-mono">
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
              <span className="text-red-400">{formatTime(elapsed)}</span>
              <span className="text-zinc-500">/ {formatTime(maxSeconds)}</span>
            </div>
            {/* Live level bars */}
            <div className="flex items-end gap-0.5 h-6">
              {levelBars.map((on, i) => (
                <div
                  key={i}
                  className={`w-1 rounded-sm transition-all ${on ? A.bg : 'bg-zinc-700'}`}
                  style={{ height: `${20 + i * 3}%` }}
                />
              ))}
            </div>
          </>
        )}

        {(status === 'recorded' || status === 'playing') && recording && (
          <>
            <button
              type="button"
              onClick={togglePlay}
              className={`inline-flex items-center gap-2 px-4 py-2 ${A.bg} ${A.hover} text-zinc-950 text-sm font-semibold rounded-lg transition-colors`}
              data-testid="voice-play-toggle"
            >
              {status === 'playing'
                ? <><Pause className="w-3.5 h-3.5" strokeWidth={2.5} /> Pause</>
                : <><Play className="w-3.5 h-3.5" strokeWidth={2.5} /> Écouter</>}
            </button>
            <span className="text-xs text-zinc-400 font-mono">{formatTime(recording.duration || 0)}</span>
            <button
              type="button"
              onClick={clearRecording}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800/60 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 text-xs font-medium rounded-lg transition-colors"
              data-testid="voice-record-clear"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Réenregistrer
            </button>
            <audio
              ref={audioRef}
              src={recording.url}
              onEnded={() => setStatus('recorded')}
              preload="metadata"
              className="hidden"
            />
          </>
        )}
      </div>

      {/* Progress bar in recording mode */}
      {status === 'recording' && (
        <div className="mt-3 w-full bg-zinc-900 rounded-full h-1 overflow-hidden">
          <div
            className={`h-1 ${A.bg} transition-all`}
            style={{ width: `${Math.min(100, (elapsed / maxSeconds) * 100)}%` }}
          />
        </div>
      )}

      {error && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-400">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <p className="mt-3 text-[11px] text-zinc-600 leading-relaxed">
        En enregistrant, tu acceptes que ton audio soit écouté par l'équipe de recrutement
        puis supprimé après décision.
      </p>
    </div>
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result || '';
      // Strip "data:audio/webm;base64," prefix
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
